import { createSlice } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import { fetchPropertyManagementByIdAsync } from "./propertyManagementApi";
import type { RootState } from "@/store/store";
import { serializeError } from "../../functions/serializeError";

// async thunk: fetch property management by id
export const fetchPropertyManagementThunk = createAppAsyncThunk(
  "propertyManagement/fetchById",
  async (propertyManagementId: number, thunkAPI) => {
    try {
      const response =
        await fetchPropertyManagementByIdAsync(propertyManagementId);

      if (response && response.status === 200 && response.data) {
        return response.data;
      }

      return thunkAPI.rejectWithValue(response);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(serializeError(err));
    }
  },
);

// Derive PropertyManagement type from API response
export type PropertyManagement = Awaited<
  ReturnType<typeof fetchPropertyManagementByIdAsync>
>["data"];

type PropertyManagementState = Partial<PropertyManagement> & {
  isLoading: boolean;
};

const initialState: PropertyManagementState = { isLoading: true };

const slice = createSlice({
  name: "propertyManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPropertyManagementThunk.pending, (state) => ({
      ...state,
      isLoading: true,
    }));
    builder.addCase(fetchPropertyManagementThunk.fulfilled, (state, action) => {
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      };
    });
    builder.addCase(fetchPropertyManagementThunk.rejected, (state) => ({
      ...state,
      isLoading: false,
    }));
  },
});

const selectSelf = (state: RootState) => state.propertyManagement;
const selectPropertyManagement = (state: RootState) => selectSelf(state);

export const propertyManagementSelectors = { selectPropertyManagement };

export const reducer = slice.reducer;
